import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { ApiService } from 'app/shared/api.service';
import { environment } from 'environments/environment';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private api: ApiService,
        private key: StorageService,
        private router: Router
    ) {}

    get accessToken(): string {
        const encryptedToken = localStorage.getItem('a_id');
        if (encryptedToken) {
            try {
                return JSON.parse(this.key.decryptionAES(encryptedToken));
            } catch (e) {
                console.error('Failed to decrypt token:', e);
                return '';
            }
        }
        return '';
    }

    /**
     * Sign in
     */
    async signIn(credentials: {
        email: string;
        password: string;
    }): Promise<{ success: boolean; needsOutletSelection: boolean; outlets: any[]; mustChangePassword: boolean }> {
        if (this._authenticated) {
            await this.signOut().toPromise();
        }

        const payload = {
            email: credentials.email,
            password: credentials.password,
        };

        try {
            const response: any = await this.api.post(
                environment.auth,
                payload,
                false
            );

            console.log('Login Response:', response);

            // Handle new API response format
            if (response && (response.status === 'success' || response.success)) {
                const token = response.data?.Authorization || response.data?.access_token;
                const clientSecret = response.data?.ClientSecret;
                const time = response.data?.time;
                const userFromApi = response.data?.user;

                if (!token) {
                    throw new Error('Invalid response from server - no token received');
                }

                // Parse JWT to get expiration (default 24 hours if not parseable)
                let expiresIn = 86400; // 24 hours default
                try {
                    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                    if (tokenPayload.exp) {
                        expiresIn = tokenPayload.exp - Math.floor(Date.now() / 1000);
                    }
                } catch (e) {
                    console.warn('Could not parse JWT expiration, using default');
                }

                const expiredAt = Date.now() + expiresIn * 1000;

                // Clear old auth tokens BEFORE generating new encryption key
                // This prevents stale tokens (encrypted with old key) from causing
                // decryption failures when user re-opens app without logging out
                localStorage.removeItem('a_id');
                localStorage.removeItem('a_exp');
                localStorage.removeItem('c_secret');
                localStorage.removeItem('a_time');
                localStorage.removeItem('s_id');
                localStorage.removeItem('user');

                // Generate new encryption key from token
                const keys = this.key.key(JSON.stringify(token));
                localStorage.setItem('s_id', keys);

                // Store encrypted token and client secret
                localStorage.setItem('a_exp', this.key.encryptData(expiredAt));
                localStorage.setItem('a_id', this.key.encryptData(token));
                if (clientSecret) {
                    localStorage.setItem('c_secret', this.key.encryptData(clientSecret));
                }
                if (time) {
                    localStorage.setItem('a_time', this.key.encryptData(time));
                }

                // Extract user ID from JWT
                let userId = '';
                try {
                    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                    if (tokenPayload.data?.userID) {
                        userId = tokenPayload.data.userID;
                    }
                } catch (e) {
                    console.warn('Could not parse user ID from JWT');
                }

                // Build user data from response
                const roles = userFromApi?.roles || [];
                const permissions = userFromApi?.permissions || [];
                const outlets = userFromApi?.outlets || [];

                const userData = {
                    id: userId,
                    name: userFromApi?.name || 'User',
                    username: userFromApi?.email || credentials.email,
                    email: userFromApi?.email || credentials.email,
                    roles: roles,
                    permissions: permissions,
                    avatar: '',
                    status: 'login',
                };

                // Save encrypted user and outlets
                localStorage.setItem('user', this.key.encryptData(userData));
                this.key.setOutlets(outlets);
                this._userService.user = userData;
                this._authenticated = true;

                // Start refresh token scheduler
                if (this.api.startTokenScheduler) {
                    this.api.startTokenScheduler(expiresIn);
                }

                // Detect default password or API flag
                const mustChangePassword = credentials.password === 'password' 
                    || userFromApi?.must_change_password === true;
                if (mustChangePassword) {
                    localStorage.setItem('must_change_pwd', 'true');
                } else {
                    localStorage.removeItem('must_change_pwd');
                }

                // If only 1 outlet, auto-select it
                if (outlets.length === 1) {
                    this.key.setOutlet(outlets[0]);
                    return { success: true, needsOutletSelection: false, outlets: [], mustChangePassword };
                }

                // If multiple outlets, need selection
                if (outlets.length > 1) {
                    return { success: true, needsOutletSelection: true, outlets: outlets, mustChangePassword };
                }

                // No outlets
                return { success: true, needsOutletSelection: false, outlets: [], mustChangePassword };
            }

            // Handle error response
            const errorMsg = response?.message || 'Login failed';
            throw new Error(errorMsg);
        } catch (error: any) {
            console.error('Login failed:', error);

            let errorMessage = 'Login failed. Please check your credentials.';

            if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Mock Sign in for development
     */
    async mockSignIn(credentials: {
        email: string;
        password: string;
    }): Promise<boolean> {
        if (this._authenticated) {
            await this.signOut().toPromise();
        }

        console.log('Using Mock Login Bypass');

        // Mock success
        const token = 'mock-development-token-12345';
        const expiresIn = 3600; // 1 hour
        const expiredAt = Date.now() + expiresIn * 1000;

        // Store encrypted token
        localStorage.setItem('a_exp', this.key.encryptData(expiredAt));
        localStorage.setItem('a_id', this.key.encryptData(token));

        // Mock permissions
        const permissions = ['tenant.loket', 'tenant.admin'];

        const userData = {
            id: 'mock-user-id-001',
            name: 'Dev User',
            username: credentials.email || 'devuser',
            email: 'dev@example.com',
            role: 'admin',
            avatar: '',
            status: 'login',
            is_tenant: true,
            permissions: permissions,
            gates: [], // Can add mock gates if needed
            // Untuk roleguard
            has_parking_access: true,
            is_tenant_admin: true,
        };

        // Save encrypted user
        localStorage.setItem('user', this.key.encryptData(userData));
        this._userService.user = userData;
        this._authenticated = true;

        return true;
    }

    /**
     * Sign in using token (auto login)
     */
    signInUsingToken(): Observable<boolean> {
        const tokenEnc = localStorage.getItem('a_id');
        const userEnc = localStorage.getItem('user'); // GANTI KEY JADI 'user'

        if (tokenEnc && userEnc) {
            try {
                const token = this.key.decryptionAES(tokenEnc);
                const user = JSON.parse(this.key.decryptionAES(userEnc));

                // console.log('Auto login with user:', user);

                // Pastikan user punya role
                if (!user.role) {
                    user.role = 'user';
                }

                this._userService.user = user;
                this._authenticated = true;

                return of(true);
            } catch (e) {
                console.error('Token decryption failed:', e);
                this.signOut();
                return of(false);
            }
        }
        return of(false);
    }

    /**
     * Sign out - HIT API LOGOUT
     */
    signOut(): Observable<boolean> {
        return new Observable((observer) => {
            if (this.accessToken) {
                this.api
                    .post('api/v1/auth/logout', {}, true)
                    .then((response: any) => {
                        console.log('Logout API Response:', response);
                        this.clearAuthData();
                        observer.next(true);
                        observer.complete();
                    })
                    .catch((error) => {
                        console.error('Logout API Error:', error);
                        this.clearAuthData();
                        observer.next(true);
                        observer.complete();
                    });
            } else {
                this.clearAuthData();
                observer.next(true);
                observer.complete();
            }
        });
    }

    /**
     * Clear authentication data
     */
    private clearAuthData(): void {
        localStorage.clear();
        this._authenticated = false;
        this._userService.user = null;
    }

    /**
     * Check authentication status
     */
    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        if (!this.accessToken) {
            return of(false);
        }

        return this.signInUsingToken();
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this._authenticated;
    }

    /**
     * Get current logged in user
     */
    getCurrentUser(): any {
        const userEnc = localStorage.getItem('user'); // KEY HARUS 'user'
        if (!userEnc) {
            console.log('No user data in localStorage');
            return null;
        }

        try {
            const user = JSON.parse(this.key.decryptionAES(userEnc));
            return user;
        } catch (error) {
            console.error('Error decrypting user data:', error);
            return null;
        }
    }

    /**
     * Get user name
     */
    getUserName(): string {
        const user = this.getCurrentUser();
        return user?.name || 'Guest';
    }

    /**
     * Get specific user property
     */
    getUserProperty(property: string): any {
        const user = this.getCurrentUser();
        return user ? user[property] : null;
    }

    /**
     * Check if user is logged in
     */
    isLoggedIn(): boolean {
        return !!this.getCurrentUser();
    }

    /**
     * Get user gates
     */
    getUserGates(): any[] {
        const user = this.getCurrentUser();
        return user?.gates || [];
    }

    /**
     * Check if user has specific gate
     */
    hasGate(gateId: string): boolean {
        const gates = this.getUserGates();
        return gates.some((gate) => gate.id === gateId);
    }
}
